import useSWRMutation from "swr/mutation";
import { ServicesURL } from "@utils/utilsApi";
import { fetcher } from "src/services/config";
import { MethodType } from "src/services/type";
import { EditionDetail, EditionEnrolledUser, EditionKind } from "../types";

const OUTLOOK_DEEPLINK_BASE =
  "https://outlook.office.com/mail/deeplink/compose?to=";
const URL_LIMIT = 4000;

const buildMailtoDeeplink = (
  users: EditionEnrolledUser[],
  edition?: EditionDetail,
): string => {
  const userEmails = users.map((u) => u.email).filter(Boolean) as string[];
  const sessions = edition?.sessions ?? [];
  const facultyEmails = sessions.flatMap((s) =>
    (s.faculties ?? []).map((f) => f.email),
  );
  const tutorEmails = sessions.flatMap((s) =>
    (s.tutors ?? []).map((tt) => tt.email),
  );

  const allEmails = Array.from(
    new Set(
      [...userEmails, ...facultyEmails, ...tutorEmails].filter(
        (e): e is string => Boolean(e),
      ),
    ),
  ).join(",");

  return OUTLOOK_DEEPLINK_BASE + allEmails;
};

interface UseEditionMailtoArgs {
  corporateId: string | number;
}

interface TriggerArgs {
  editionId: string | number;
  kind: EditionKind;
}

export type MailtoOutcome = "OPENED" | "URL_TOO_LONG" | "ERROR";

export const useEditionMailto = ({ corporateId }: UseEditionMailtoArgs) => {
  const { trigger, isMutating } = useSWRMutation(
    "edition-mailto",
    async (_key: string, { arg }: { arg: TriggerArgs }) => {
      const detail = await fetcher<EditionDetail>(
        `${ServicesURL.getEditionsDetail}/${arg.kind}/${arg.editionId}?corporateId=${corporateId}`,
        MethodType.GET,
      );
      const usersResponse = await fetcher<{ users: EditionEnrolledUser[] }>(
        `${ServicesURL.getEnrollEditionDetail}?corporateId=${corporateId}&editionId=${arg.editionId}&showIssuedCertificate=true&pageIndex=0&pageSize=200`,
        MethodType.GET,
      );
      return buildMailtoDeeplink(usersResponse?.users ?? [], detail);
    },
  );

  const sendMail = async (args: TriggerArgs): Promise<MailtoOutcome> => {
    try {
      const deepLink = await trigger(args);
      if (!deepLink) return "ERROR";
      if (deepLink.length > URL_LIMIT) return "URL_TOO_LONG";
      window.open(deepLink, "_blank");
      return "OPENED";
    } catch {
      return "ERROR";
    }
  };

  return { sendMail, isPreparing: isMutating };
};
